import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone, MessageCircle, ArrowRight, ShieldCheck, BookOpen, Users, Heart,
  Monitor, Sparkles, Scale, GraduationCap, Wallet, Star,
} from "lucide-react";
import { SITE, IMAGES } from "@/constants/site";
import { Reveal, MaskedLine, Stagger, StaggerItem, Squiggle } from "@/components/site/Reveal";
import { EditorialMarquee } from "@/components/site/Marquee";
import { NewsSection } from "@/components/site/NewsSection";

const TRUST = [
  { icon: ShieldCheck, label: "Govt. Certified", sub: "Since 2012", color: "text-brand-deepsky bg-brand-sky/15" },
  { icon: BookOpen, label: "RBSE Based", sub: "Pattern", color: "text-brand-deepcoral bg-brand-coral/15" },
  { icon: Users, label: "Low Student-", sub: "Teacher Ratio", color: "text-amber-600 bg-brand-sun/20" },
  { icon: Heart, label: "Safe &", sub: "Nurturing", color: "text-emerald-600 bg-emerald-100" },
];

const PROGRAMS_PREVIEW = [
  { name: "Toddler", age: "18 months – 3 yrs", img: IMAGES.toddler, desc: "Gentle first steps — sensory play, rhymes and warm routines.", accent: "bg-brand-sky" },
  { name: "Preschool", age: "3 – 4 yrs", img: IMAGES.art, desc: "Language blooms through stories, phonics and pretend play.", accent: "bg-brand-coral" },
  { name: "Pre-K", age: "4+ yrs", img: IMAGES.circle, desc: "School-ready readers, writers and curious little mathematicians.", accent: "bg-brand-sun" },
];

const WHY = [
  { icon: Monitor, title: "Smart Classrooms", desc: "Audio-visual learning that makes every concept come alive." },
  { icon: Sparkles, title: "Holistic Learning", desc: "Mind, body and values grow together — not just marks." },
  { icon: Scale, title: "Discipline & Values", desc: "Sanskars and good habits woven gently into every day." },
  { icon: GraduationCap, title: "Experienced Faculty", desc: "Loving teachers trained in early-childhood pedagogy." },
  { icon: Wallet, title: "Affordable Fees", desc: "Quality education every family in Murlipura can reach." },
];

const GALLERY = [IMAGES.hero, IMAGES.diyas, IMAGES.art, IMAGES.circle, IMAGES.christmas, IMAGES.outing, IMAGES.preschool];

const TESTIMONIALS = [
  {
    quote: "My daughter runs to school every morning. The teachers know every child by name — it truly feels like family.",
    name: "Priya Sharma", role: "Nursery Parent", color: "bg-brand-sky",
  },
  {
    quote: "Within months my son started reading on his own. The RBSE pattern here is strong, but never pressurising.",
    name: "Rakesh Meena", role: "Pre-K Parent", color: "bg-brand-coral",
  },
  {
    quote: "Safe campus, loving teachers, and regular updates. The best decision we made in Murlipura.",
    name: "Neha & Amit Gupta", role: "Toddler Parents", color: "bg-brand-sun",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <main data-testid="home-page" className="pb-20 md:pb-0">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden pt-32 md:pt-40 grain">
        <motion.div style={{ y: blobY }} className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-brand-sky/20 blur-3xl" />
        <motion.div style={{ y: blobY }} className="pointer-events-none absolute top-1/3 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-sun/25 blur-3xl" />
        <div className="pointer-events-none absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-brand-coral/15 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2">
          <div>
            <Reveal y={16}>
              <span data-testid="hero-admission-chip" className="inline-flex items-center gap-2 rounded-full border border-brand-sun/60 bg-brand-sun/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-amber-700">
                <Star className="h-3.5 w-3.5 fill-brand-sun text-brand-sun" /> Admissions Open 2026–27
              </span>
            </Reveal>

            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight">
              <MaskedLine delay={0.15}>Nurturing Minds,</MaskedLine>
              <MaskedLine delay={0.35}>
                <span className="relative inline-block text-brand-deepcoral">
                  Shaping Futures
                  <Squiggle className="absolute -bottom-3 left-0 w-full text-brand-sun" />
                </span>
                <span className="text-brand-deepsky">.</span>
              </MaskedLine>
            </h1>

            <Reveal delay={0.6} className="mt-8 max-w-md">
              <p className="text-base md:text-lg leading-relaxed text-slate-600">
                A warm, play-based preschool & primary school in Murlipura, Jaipur — where children aged 18 months to 5+ years
                learn through joy, values and discovery.
              </p>
            </Reveal>

            <Reveal delay={0.75} className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/admissions"
                data-testid="hero-enquire-btn"
                className="group inline-flex min-h-[52px] items-center gap-2 rounded-full bg-brand-coral px-7 font-bold text-white shadow-[0_10px_30px_rgba(251,113,133,0.45)] transition-transform duration-300 hover:scale-105"
              >
                Enquire Now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${SITE.phone1}`}
                data-testid="hero-call-btn"
                className="inline-flex min-h-[52px] items-center gap-2 rounded-full border-2 border-brand-sky bg-white/70 px-6 font-bold text-brand-deepsky transition-all duration-300 hover:bg-brand-sky hover:text-white"
              >
                <Phone className="h-4 w-4" /> Call
              </a>
              <a
                href={SITE.whatsappLink}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-emerald-500 px-6 font-bold text-white shadow-[0_10px_30px_rgba(16,185,129,0.35)] transition-transform duration-300 hover:scale-105"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </Reveal>
          </div>

          <motion.div style={{ y: imgY }} className="relative mx-auto w-full max-w-lg">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="blob-a overflow-hidden border-8 border-white shadow-[0_30px_80px_rgba(2,132,199,0.25)]">
                <img src={IMAGES.hero} alt="Children playing with bubbles in the school garden" className="aspect-[4/5] w-full object-cover" data-testid="hero-image" />
              </div>
              <div className="absolute -top-6 -right-4 animate-float-slow rounded-3xl bg-white px-5 py-3 shadow-xl">
                <p className="font-display text-2xl font-semibold text-brand-deepcoral">Since 2012</p>
                <p className="text-xs font-semibold text-slate-500">14+ years of trust</p>
              </div>
              <div className="absolute -bottom-5 -left-4 animate-float-slow rounded-3xl bg-brand-navy px-5 py-3 text-white shadow-xl [animation-delay:1.2s]">
                <p className="font-display text-lg font-semibold text-brand-sun">RBSE Based Pattern</p>
                <p className="text-xs text-sky-200">Govt. Certified School</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* TRUST STRIP */}
        <div className="relative mx-auto mt-16 max-w-6xl px-6 pb-4">
          <Stagger className="grid grid-cols-2 gap-4 rounded-[2.5rem] border border-amber-200/50 bg-white/80 p-6 shadow-[0_20px_60px_rgba(2,132,199,0.1)] backdrop-blur md:grid-cols-4" >
            {TRUST.map((t) => (
              <StaggerItem key={t.label} className="flex items-center gap-4 p-2" >
                <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${t.color}`}>
                  <t.icon className="h-6 w-6" />
                </span>
                <p className="font-display text-sm md:text-base font-semibold leading-tight">
                  {t.label} <span className="block text-slate-500 font-body text-xs font-semibold">{t.sub}</span>
                </p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <EditorialMarquee />

      {/* PROGRAMS PREVIEW */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <Reveal className="mb-14 max-w-2xl">
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-3">Our Programs</p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">A class for every little milestone</h2>
        </Reveal>
        <Stagger className="grid gap-8 md:grid-cols-3">
          {PROGRAMS_PREVIEW.map((p, i) => (
            <StaggerItem key={p.name}>
              <Link to="/programs" data-testid={`program-card-${p.name.toLowerCase().replace(/[^a-z]/g, "-")}`} className="group block">
                <div className={`relative overflow-hidden rounded-[2.5rem] ${i % 2 ? "blob-b" : "blob-a"} border-4 border-white shadow-[0_20px_50px_rgba(2,132,199,0.12)]`}>
                  <img src={p.img} alt={p.name} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <span className={`absolute top-4 left-4 rounded-full ${p.accent} px-4 py-1.5 text-xs font-bold text-white shadow-lg`}>{p.age}</span>
                </div>
                <div className="px-3 pt-5">
                  <h3 className="font-display text-2xl font-semibold transition-colors group-hover:text-brand-deepsky">{p.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">{p.desc}</p>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* WHY CHOOSE US */}
      <section className="relative bg-brand-navy py-24 md:py-32 grain overflow-hidden">
        <div className="pointer-events-none absolute -top-20 right-0 h-80 w-80 rounded-full bg-brand-sky/20 blur-3xl" />
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-sun mb-3">Why Choose Us</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white">The Blooming Chaitanya difference</h2>
          </Reveal>
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((w, i) => (
              <StaggerItem key={w.title} className={i === 0 ? "lg:row-span-1" : ""}>
                <div data-testid={`why-card-${w.title.toLowerCase().replace(/[^a-z]/g, "-")}`} className="group h-full rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur transition-all duration-300 hover:-translate-y-2 hover:bg-white/10">
                  <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-sun text-ink transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
                    <w.icon className="h-7 w-7" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold text-white">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-sky-200/80">{w.desc}</p>
                </div>
              </StaggerItem>
            ))}
            <StaggerItem>
              <Link to="/admissions" data-testid="why-cta-card" className="group flex h-full flex-col justify-between rounded-3xl bg-brand-coral p-8 shadow-[0_20px_50px_rgba(251,113,133,0.35)] transition-transform duration-300 hover:-translate-y-2">
                <p className="font-hand text-3xl text-white/95">Come, see the magic yourself!</p>
                <span className="mt-6 inline-flex items-center gap-2 font-bold text-white">
                  Book a visit <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </StaggerItem>
          </Stagger>
        </div>
      </section>

      {/* PRINCIPAL SNAPSHOT */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="relative mx-auto w-64 md:w-80">
              <div className="blob-c overflow-hidden border-8 border-white shadow-[0_25px_60px_rgba(2,132,199,0.35)]">
                <img src={IMAGES.principal} alt="Ram Ratan Saini, Principal of Blooming Chaitanya Academy" className="aspect-square w-full object-cover" data-testid="principal-photo" />
              </div>
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-sun px-6 py-2 font-display font-semibold text-ink shadow-xl">
                Ram Ratan Saini
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-3">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-4">From the Principal's Desk</p>
            <blockquote data-testid="principal-quote" className="font-hand text-3xl md:text-5xl leading-snug text-ink">
              "Every child carries a spark. Our work is simply to give it warmth, patience and room to shine."
            </blockquote>
            <p className="mt-6 text-sm font-semibold text-slate-500">Ram Ratan Saini · Principal, Blooming Chaitanya Academy</p>
            <Link to="/about" data-testid="principal-read-more" className="group mt-6 inline-flex items-center gap-2 font-bold text-brand-deepcoral">
              Read the full message <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <Reveal className="mb-14 max-w-2xl">
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-3">Happy Parents</p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">Words that warm our hearts</h2>
        </Reveal>
        <Stagger className="grid gap-8 md:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <StaggerItem key={t.name}>
              <figure data-testid={`testimonial-${t.name.toLowerCase().replace(/[^a-z]/g, "-")}`} className="group relative h-full rounded-[2.5rem] border border-amber-200/50 bg-white p-8 pt-12 shadow-[0_15px_40px_rgba(2,132,199,0.08)] transition-all duration-300 hover:-translate-y-2">
                <span className={`absolute -top-5 left-8 grid h-11 w-11 place-items-center rounded-full ${t.color} font-hand text-3xl text-white shadow-lg`}>"</span>
                <blockquote className="font-hand text-2xl leading-snug text-slate-700">{t.quote}</blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <span className={`grid h-11 w-11 place-items-center rounded-full ${t.color}/20 font-display font-bold text-ink`}>
                    {t.name.charAt(0)}
                  </span>
                  <span>
                    <span className="block font-display font-semibold">{t.name}</span>
                    <span className="block text-xs font-semibold text-slate-500">{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* NEWS & EVENTS */}
      <NewsSection />

      {/* GALLERY STRIP */}
      <section className="py-10 overflow-hidden">
        <Reveal className="mx-auto mb-10 max-w-7xl px-6">
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-3">Campus Life</p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">Little moments, big memories</h2>
        </Reveal>
        <div data-testid="gallery-strip" className="flex gap-6 overflow-x-auto px-6 pb-8 pt-4 snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {GALLERY.map((src, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40, rotate: 0 }}
              whileInView={{ opacity: 1, y: 0, rotate: i % 2 ? 2 : -2 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              whileHover={{ rotate: 0, scale: 1.04 }}
              className={`shrink-0 snap-center overflow-hidden border-4 border-white shadow-xl ${i % 2 ? "blob-b" : "blob-a"} w-64 md:w-80`}
            >
              <img src={src} alt={`Campus life ${i + 1}`} className="aspect-[4/5] w-full object-cover" loading="lazy" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="mx-auto max-w-7xl px-6 pt-20">
        <Reveal>
          <div data-testid="home-cta-banner" className="relative overflow-hidden rounded-[3rem] bg-brand-coral px-8 py-16 text-center shadow-[0_30px_80px_rgba(251,113,133,0.35)] md:py-20 grain">
            <div className="pointer-events-none absolute -top-16 -left-16 h-56 w-56 rounded-full bg-brand-sun/40 blur-2xl" />
            <h2 className="font-display text-4xl md:text-6xl font-semibold text-white tracking-tight">Admission Open — Limited Seats</h2>
            <p className="mx-auto mt-4 max-w-xl text-white/90">Give your child a joyful start. Seats fill fast for the new session — enquire today.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/admissions" data-testid="cta-banner-enquire" className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-white px-8 font-bold text-brand-deepcoral transition-transform duration-300 hover:scale-105">
                Enquire Now <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={`tel:${SITE.phone1}`} data-testid="cta-banner-call" className="inline-flex min-h-[52px] items-center gap-2 rounded-full border-2 border-white/70 px-8 font-bold text-white transition-colors duration-300 hover:bg-white/10">
                <Phone className="h-4 w-4" /> {SITE.phone1Display}
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
